import { Target, Compass, Handshake, ShieldCheck, TrendingUp, Award, Users } from "lucide-react";
import PageHero from "@/components/PageHero";
import SectionHeading from "@/components/SectionHeading";
import CTABand from "@/components/CTABand";
import CertificationsSection from "@/components/CertificationsSection";
import TestimonialsSection from "@/components/TestimonialsSection";
import DownloadProfileButton from "@/components/DownloadProfileButton";
import { COMPANY, VALUES } from "@/data/company";
import { INDUSTRIES } from "@/data/clients";

const VALUE_ICONS = {
  Integrity: ShieldCheck,
  Quality: Award,
  "Customer Commitment": Users,
  Reliability: Handshake,
  Innovation: TrendingUp,
  Excellence: Target,
  Partnership: Handshake,
};

export default function About() {
  return (
    <div>
      <PageHero
        eyebrow="About us"
        title="A trusted industrial solutions partner — since 2016."
        description="Headquartered in Belagavi, Karnataka, Nago International supplies industries across India with quality products, technical expertise and dependable supply-chain solutions."
        image="https://images.unsplash.com/photo-1716643863806-989dd76ae093?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000"
      />

      <section className="section bg-white">
        <div className="container-nago grid lg:grid-cols-5 gap-12">
          <div className="lg:col-span-3">
            <div className="eyebrow">Who we are</div>
            <h2 className="mt-3 font-display font-extrabold text-3xl sm:text-4xl text-slate-900 leading-tight">
              A professionally managed industrial supply company.
            </h2>
            <div className="mt-6 space-y-5 text-slate-700 leading-relaxed">
              <p>
                Nago International is a professionally managed industrial solutions and supply
                company headquartered in Belagavi, Karnataka. Since 2016, we have supported
                industries with high-quality products, technical expertise and dependable
                supply-chain solutions.
              </p>
              <p>
                As authorized dealers and distributors of leading industrial lubricant brands —
                alongside a complete range of tools, hardware, paints, safety equipment and
                engineering consumables — we give our customers a reliable single-source
                solution for industrial procurement.
              </p>
              <p>
                We also design and manufacture customized industrial machines, and partner with
                clients through technical support, preventive maintenance and product-performance
                monitoring that keep equipment running efficiently.
              </p>
            </div>
            <div className="mt-8 flex flex-wrap gap-3">
              <DownloadProfileButton size="lg" testId="about-download-profile-btn" />
            </div>
          </div>

          <div className="lg:col-span-2 space-y-6">
            <div className="card-industrial border-l-4 border-[#E30613]">
              <div className="flex items-center gap-3">
                <Compass className="w-6 h-6 text-[#E30613]" />
                <h3 className="font-display font-bold text-lg text-slate-900">Mission</h3>
              </div>
              <p className="mt-3 text-sm text-slate-600 leading-relaxed">
                To provide industries with reliable access to high-quality lubricants, metal
                cutting tools, industrial consumables, packaging materials, safety equipment and
                customized engineering solutions — through strong partnerships, technical
                expertise and customer-focused service.
              </p>
            </div>
            <div className="card-industrial border-l-4 border-[#1E3A8A]">
              <div className="flex items-center gap-3">
                <Target className="w-6 h-6 text-[#1E3A8A]" />
                <h3 className="font-display font-bold text-lg text-slate-900">Vision</h3>
              </div>
              <p className="mt-3 text-sm text-slate-600 leading-relaxed">
                To become a trusted and preferred industrial solutions partner across India —
                delivering quality products, innovative engineering solutions and dependable
                service that contribute to the growth and success of our customers.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="section bg-industrial-soft">
        <div className="container-nago">
          <SectionHeading
            eyebrow="Our values"
            title="Values that guide every enquiry, every order."
            align="center"
          />
          <div className="mt-12 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-4">
            {VALUES.map((v) => {
              const Icon = VALUE_ICONS[v] || Handshake;
              return (
                <div
                  key={v}
                  className="bg-white border border-slate-200 rounded-xl p-5 text-center hover:border-[#E30613] transition-colors"
                  data-testid={`value-${v.toLowerCase().replace(/\s/g, "-")}`}
                >
                  <div className="mx-auto w-10 h-10 flex items-center justify-center bg-[#1E3A8A]/10 rounded-xl">
                    <Icon className="w-5 h-5 text-[#1E3A8A]" />
                  </div>
                  <div className="mt-3 text-sm font-semibold text-slate-800">{v}</div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="section bg-white">
        <div className="container-nago">
          <SectionHeading
            eyebrow="Industries we serve"
            title="Trusted across the Indian industrial landscape."
            description="From heavy engineering and foundries to CNC machining centres, tool rooms and process industries, our supply reaches customers wherever precision and reliability matter."
          />
          <div className="mt-10 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {INDUSTRIES.map((i) => (
              <div
                key={i}
                className="bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-sm text-slate-700 font-medium hover:bg-white hover:border-[#1E3A8A] transition-colors"
                data-testid={`industry-${i.toLowerCase().replace(/\s|&/g, "-")}`}
              >
                {i}
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section bg-slate-900 text-white">
        <div className="container-nago grid lg:grid-cols-2 gap-12">
          <div>
            <div className="eyebrow text-red-400">Our promise</div>
            <h2 className="mt-3 font-display font-extrabold text-3xl sm:text-4xl">
              The right products, the right solutions, the right support — keeping industry running.
            </h2>
          </div>
          <div className="text-slate-300 leading-relaxed">
            <p>
              At Nago International, we are committed to delivering high-quality industrial
              products, dependable technical support, and innovative solutions that help
              industries improve productivity, optimize equipment performance and achieve
              sustainable growth.
            </p>
            <p className="mt-4">
              Through our network of authorized brands, an experienced team, and a
              customer-centric approach, we strive to be a trusted partner for industries
              across India.
            </p>
            <div className="mt-8 grid grid-cols-2 gap-4">
              {COMPANY.stats.slice(0, 4).map((s) => (
                <div key={s.label} className="border-l-2 border-[#E30613] pl-4">
                  <div className="font-display font-extrabold text-2xl">{s.value}</div>
                  <div className="text-xs uppercase tracking-widest text-slate-400 mt-1">
                    {s.label}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <CertificationsSection />
      <TestimonialsSection />
      <CTABand />
    </div>
  );
}
